<template>
  <div
    ref="splitpaneElement"
    class="lexmea-splitter-panes h-full min-w-0 flex-grow bg-lexmea-gray-500"
    :class="{ 'py-2 pr-2': showcase }"
  >
    <splitpanes
      class="default-theme bg-white"
      :class="{
        rounded: showcase,
      }"
      :dblClickSplitter="false"
      @resize="paneSize = $event[0].size"
      @splitterClick="handleSplitterClick"
    >
      <!-- column 2 -->
      <pane
        id="step-3"
        class="min-w-0 max-lg:!w-full max-lg:px-2 lg:min-w-[19rem]"
        :size="showcase ? 100 : paneSize"
      >
        <section class="flex h-full flex-col pl-1 pt-1 lg:px-2">
          <TabOverview
            id="step-5"
            :legalText="legalText"
            :legalTextParagraph="legalTextParagraph"
            :loading="legalTextLoading || paragraphLoading"
          />
          <div
            class="flex items-center justify-between border-b border-gray-300/75 lg:flex-col portrait:flex-col portrait:items-start"
          >
            <div
              v-if="legalText"
              class="w-full border-b border-gray-300/75 py-1 landscape:max-lg:max-w-[50%] landscape:max-lg:border-none"
            >
              <LegalTextHeader
                v-model:fullView="fullView"
                :legalText="legalText"
                :mainContent="Boolean(legalTextParagraph || active)"
              />
              <FolBreadcrumb
                class="max-md:hidden landscape:max-lg:hidden"
                :fieldOfLaw="fieldOfLaw"
                :isMainContent="!activeEntrySlug"
              />
            </div>
            <div class="flex h-full w-full items-center">
              <div
                class="mr-3 hidden h-[85%] w-[1px] border-l border-gray-300/75 landscape:max-lg:block"
              ></div>
              <div
                id="step-4"
                class="inline-flex flex-col pt-1"
                :class="{
                  'pointer-events-none opacity-25': !legalTextParagraphSlug,
                }"
              >
                <div
                  ref="toolboxWrapper"
                  v-tooltip.bottom="{
                    content: 'Du hast das Limit der Testfunktionen erreicht.',
                    disabled: !localEntries.isLimitReached.value,
                  }"
                  @click="toolsWrapperClick"
                >
                  <ToolBox
                    v-model:markerColor="markerColor"
                    :markingMode="markingMode"
                    :disabled="!legalTextParagraphSlug"
                    :bookmark="bookmark"
                    @addBookmark="addBookmark($event, legalTextParagraph!)"
                    @deleteBookmark="deleteBookmark(bookmark!.id)"
                    @update:bookmark="updateBookmark(bookmark!.id, $event)"
                    @update:bookmarkText="bookmarkText = $event"
                    @update:markingMode="updateMarkingMode"
                    @update:modelValue="updateMarkColor"
                  />
                </div>
                <p class="h-[1.1rem] text-left text-xs landscape:max-lg:hidden">
                  <Transition name="scale">
                    <template v-if="helperText || bookmarkText">
                      {{ helperText || bookmarkText }}
                    </template>
                  </Transition>
                </p>
              </div>
            </div>
          </div>
          <LegalTextTocOverview
            v-if="showOverview && nestedData"
            class="min-h-0"
            :toc="nestedData"
            :flatToc="toc!"
            :legalTextSlug="legalTextSlug!"
            :tocOverviewParagraph="tocOverviewParagraph"
          />
          <LegalTextParagraph
            v-else-if="legalText && toc"
            ref="paragraphComponent"
            v-model:markingMode="markingMode"
            v-model:movingEntry="movingEntry"
            :flatToc="toc"
            :legalText="legalText"
            :legalTextParagraph="legalTextParagraph"
            :markerColor="markerColor"
            :markerPermanent="markerPermanent"
            :isMainContent="!activeEntrySlug"
            :showLinks="true"
            :fullView
            :nextLegalTextParagraph="nextLegalTextParagraph"
            :previousLegalTextParagraph="previousLegalTextParagraph"
            :loading="paragraphLoading"
          />
        </section>
      </pane>
      <!-- column 3 -->
      <pane
        v-if="!showcase"
        id="step-6"
        class="max-lg:hidden lg:min-w-[19rem]"
        :size="100 - paneSize"
      >
        <section class="h-full pl-2 pr-1 pt-1">
          <NotesView
            v-if="activeEntryType === 'note'"
            :notes="activeNoteVersionNotes"
            :activeNoteId="active?.id"
            :notesActiveParagraph
            @close="setActive()"
            @setActive="setActive"
          />
          <template v-else-if="activeEntryType === 'schema'">
            <TextEditor
              v-if="active?.type === 'schema'"
              class="lexmea-hyphenate h-full"
              :entry="active"
              :fieldOfLaw="activeEntryFol"
              :isMainContent="!!activeEntrySlug"
              @close="setActive()"
            />
            <div v-else class="flex h-full w-full items-center justify-center">
              <i-line-md-loading-twotone-loop class="h-12 w-12" />
            </div>
          </template>
          <ul
            v-else
            class="mx-auto mt-20 flex max-w-max list-outside flex-col gap-4 rounded bg-lexmea-gray-400 p-8 pl-12"
          >
            <li
              v-if="
                activeParagraphFolSchemas &&
                activeParagraphFolSchemas.length > 0
              "
            >
              <span class="text-6pt font-bold">
                {{ t("existingSchemata.header") }}
              </span>
              <br />
              <i18n-t
                class="ml-5 text-5pt"
                tag="span"
                keypath="existingSchemata.text"
              >
                <template #clipboardIcon>
                  <ClipboardIcon class="inline-block h-4 w-4" />
                </template>
                <template #paperIcon>
                  <PaperIcon class="inline-block h-4 w-4" />
                </template>
              </i18n-t>
            </li>
            <li>
              <span class="text-6pt font-bold">
                {{ t("newSchemata.header") }}
              </span>
              <br />
              <i18n-t
                class="ml-5 text-5pt"
                tag="span"
                keypath="newSchemata.text"
              >
                <template #clipboardIcon>
                  <ClipboardIcon class="inline-block h-4 w-4" />
                </template>
                <template #paperIcon>
                  <PaperIcon class="inline-block h-4 w-4" />
                </template>
              </i18n-t>
            </li>
          </ul>
        </section>
      </pane>
    </splitpanes>
  </div>
</template>

<script lang="ts" setup>
import { Pane, Splitpanes } from "splitpanes";
import type { IFieldOfLawWithParents } from "~/models/IFieldOfLaw";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";
import type { CanBeActive, IParagraphEntry } from "~/models/IUserEntry";

type Props = {
  legalText: ILegalText | undefined;
  legalTextParagraph: ILegalTextParagraph | undefined;
  legalTextSlug: string;
  legalTextParagraphSlug: string;
  legalTextLoading: boolean;
  paragraphLoading: boolean;
  activeEntrySlug: string;
  activeEntryType: "schema" | "note" | "";
  nextLegalTextParagraph: string | undefined;
  previousLegalTextParagraph: string | undefined;
  toc: (ILegalTextParagraph | IToC)[] | undefined;
  nestedData: IToC[] | undefined;
  active: CanBeActive | undefined;
  fieldOfLaw: IFieldOfLawWithParents | undefined;
};

const { activeNoteVersionNotes, activeParagraph: notesActiveParagraph } =
  useParagraphNotes();

const props = defineProps<Props>();

const {
  legalText,
  legalTextParagraph,
  legalTextParagraphSlug,
  activeEntrySlug,
  activeEntryType,
  fieldOfLaw,
} = toRefs(props);

const { t } = useI18n();

const fieldOfLawId = computed(() => fieldOfLaw.value?.id);

const fullView = useLocalStorage("legalTextFullView", false, {
  initOnMounted: true,
});

const showcase = useExamShowcase();

const movingEntry = ref<IParagraphEntry>();
const {
  query: { data: activeEntryFol },
} = useActiveEntryFieldOfLaw();
const { setActive } = useSetActiveEntry();

const { bookmarks, addBookmark, updateBookmark, deleteBookmark } =
  useBookmarks(legalText);

const bookmark = computed(() =>
  bookmarks.value?.find(
    (b) => b.legal_text_paragraph_id === legalTextParagraph.value?.id
  )
);

const bookmarkText = ref<string | null>(null);

const {
  markerColor,
  markingMode,
  markerPermanent,
  updateMarkingMode,
  updateMarkColor,
} = useMarkingMode();

watch(markerPermanent, () => {
  movingEntry.value = undefined;
});

const { helperText } = useHelperText(markingMode, movingEntry);

const { splitpaneElement, paneSize, handleSplitterClick } = useSplitpane();

const { warning } = useToast();

const localEntries = useLocalEntriesSync();

const toolsWrapperClick = () => {
  if (localEntries.isLimitReached.value) {
    warning("Du hast das Limit der Testfunktionen ohne Account erreicht.");
  }
};

watchEffect(() => {
  if (localEntries.isLimitReached.value) {
    updateMarkingMode();
  }
});

const paragraphComponent = ref<HTMLElement>();
const toolboxWrapper = ref();
const selectionStore = useSelectionStore();

onClickOutside(
  paragraphComponent,
  () => {
    // this is how onClickHandler works internally... for some reason it checks event.detail === 0 which is not true for click events
    // so just do the check ourselves
    selectionStore.resetSelection(false);
    updateMarkingMode();
  },
  { ignore: [toolboxWrapper, ".vfm"] }
);

const showOverview = computed(
  () =>
    !legalTextParagraphSlug.value &&
    props.nestedData &&
    props.nestedData.length > 0
);

const tocOverviewParagraph = ref<ILegalTextParagraph>();

/**
 * We're doing this within a single watcher as the past implementation
 * would interfere with one another
 */
watchEffect(() => {
  if (!showOverview.value) {
    tocOverviewParagraph.value = undefined;
  }
  if (legalTextParagraph.value) {
    const pId = legalTextParagraph.value.id;
    tocOverviewParagraph.value = props.toc?.find(
      (tocItem) => "paragraph" in tocItem && tocItem.id === pId
    ) as ILegalTextParagraph | undefined;
  }
});

const {
  data: activeParagraphFolSchemas,
  query: paragraphFolSchemasQuery,
  enabled,
} = useSchemasForFieldOfLaw(fieldOfLawId);

useSafeQuerySuspense({ enabled, suspense: paragraphFolSchemasQuery.suspense });
</script>

<i18n>
{
  "de": {
    "newSchemata": {
      "header": "Hier neue Schemata oder Notizen anlegen",
      "text": "... durch Klicken auf ein {clipboardIcon} oder {paperIcon} in der Symbolleiste über dem Gesetzestext."
    },
    "existingSchemata": {
      "header": "Hier vorhandene Schemata / Notizen anzeigen",
      "text": "... durch Klicken auf das {clipboardIcon} oder {paperIcon} im Gesetzestext oder in der Übersichts-Spalte rechts."
    }
  },
  "en": {
    "newSchemata": {
      "header": "Create new @.lower:documents or notes",
      "text": "... by clicking on {clipboardIcon} or {paperIcon} in the toolbar above the legal text."
    },
    "existingSchemata": {
      "header": "Show existing @:documents or notes",
      "text": "... by clicking on {clipboardIcon} or {paperIcon} in the legal text on the overview column on the right."
    }
  }
}
</i18n>
